/**
 * Knowledge document workflow_publish API — Document Layer I4
 */

import type {
  DocumentExecutorInput,
  DocumentExecutorResult,
} from '@shared/document/resolve-executor';
import { API_REQUEST_TIMEOUT_MS } from '../config/api';
import { getAuthHeaders } from '../utils/jwtUtils';
import { fetchWithAuth } from './authFetch';

const KNOWLEDGE_BASE = '/api/domain';

async function postPublish<T>(
  tenantId: string,
  path: string,
  body: Record<string, unknown>
): Promise<T> {
  const res = await fetchWithAuth(path, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-tenant-id': tenantId,
      ...getAuthHeaders(),
    },
    body: JSON.stringify(body),
    signal: AbortSignal.timeout(API_REQUEST_TIMEOUT_MS),
  });
  const json = (await res.json()) as { success?: boolean; data?: T; error?: string };
  if (!res.ok || json.success === false) {
    throw new Error(json.error ?? `Workflow publish failed: ${res.status}`);
  }
  return json.data as T;
}

export async function publishKnowledgePolicy(
  tenantId: string,
  policyId: string,
  params?: { publishedBy?: string }
): Promise<unknown> {
  return postPublish(
    tenantId,
    `${KNOWLEDGE_BASE}/policy/ops/${encodeURIComponent(policyId)}/publish`,
    { ...(params?.publishedBy ? { publishedBy: params.publishedBy } : {}) }
  );
}

export async function publishKnowledgePlan(
  tenantId: string,
  planId: string,
  params?: { publishedBy?: string }
): Promise<unknown> {
  return postPublish(tenantId, `${KNOWLEDGE_BASE}/plan/ops/${encodeURIComponent(planId)}/publish`, {
    ...(params?.publishedBy ? { publishedBy: params.publishedBy } : {}),
  });
}

export async function publishKnowledgeDocument(
  input: DocumentExecutorInput
): Promise<DocumentExecutorResult> {
  const recordId = String(
    input.payload.recordId ?? input.payload.documentId ?? input.payload.id ?? ''
  );

  if (!recordId) {
    return { ok: false, error: 'recordId required for workflow publish' };
  }

  try {
    if (input.documentType === 'knowledge_policy') {
      const actorId = typeof input.payload.actorId === 'string' ? input.payload.actorId : undefined;
      const data = await publishKnowledgePolicy(
        input.tenantId,
        recordId,
        actorId ? { publishedBy: actorId } : undefined
      );
      return { ok: true, data };
    }

    if (input.documentType === 'knowledge_plan') {
      const actorId = typeof input.payload.actorId === 'string' ? input.payload.actorId : undefined;
      const data = await publishKnowledgePlan(
        input.tenantId,
        recordId,
        actorId ? { publishedBy: actorId } : undefined
      );
      return { ok: true, data };
    }

    return {
      ok: false,
      error: `Unsupported workflow_publish document type: ${input.documentType}`,
    };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Workflow publish failed';
    return { ok: false, error: message };
  }
}
